// Catalog.ID Client — Kit / Terms Merge (pure)
// ============================================================================
// The Membership Agreement is NOT typeset by pdfmake: it is a pre-rendered
// asset (components/terms-asset.ts) so the legal text is reviewed and shipped
// as one artefact instead of being re-flowed by a layout engine. That leaves
// one job here — append it to the pdfmake output and give its pages the kit's
// own footer, so the whole document reads as one run of page numbers.
//
// The stamp has to land exactly where pdfmake puts its own footer, because a
// reader flipping from page 7 to page 8 must not see the line jump. pdfmake's
// geometry (booklet-definition.ts, LETTERHEAD_PAGE_MARGINS_A4) is:
//
//   - the footer block starts at y = pageHeight − pageMargins.bottom
//     (LayoutBuilder.addHeadersAndFooters' footerSizeFct),
//   - the text node's own top margin (FOOTER_TOP_MARGIN, shared.ts — derived
//     so the baseline lands on the letterhead's "catalog.org" row) pushes the
//     line down from there,
//   - pdfkit then draws the baseline one ascender below the line's top.
//
// Measured from the BOTTOM edge that is `bottom − topMargin − ascender`, which
// is independent of the page height — so the same number works for any page
// size the asset happens to use. The ascender comes from the very TTF pdfmake
// draws with (Roboto Regular out of its own vfs), so the two agree by
// construction rather than by a magic constant.

import { PDFDocument, type PDFFont, type PDFPage } from 'pdf-lib';
import fontkit from '@pdf-lib/fontkit';
import { LETTERHEAD_PAGE_MARGINS_A4 } from '../letterhead-background';
import {
    FOOTER_FONT_SIZE, FOOTER_TOP_MARGIN, shortKitTitle, ROBOTO_ASCENDER_PER_MILLE,
} from './shared';

export { FOOTER_FONT_SIZE } from './shared';
export const FOOTER_SIDE_MARGIN = LETTERHEAD_PAGE_MARGINS_A4[0];
const PAGE_BOTTOM_MARGIN = LETTERHEAD_PAGE_MARGINS_A4[3];

export interface KitFooterStamp {
    /** 0-based page index WITHIN the terms PDF. */
    pageIndex: number;
    text: string;
}

export interface MergeKitInput {
    /** pdfmake's bytes: the master pages followed by the unnumbered copies. */
    kitPdf: Uint8Array;
    /** The pre-rendered Membership Agreement, letterhead painted, no footer. */
    termsPdf: Uint8Array;
    /** Numbered master pages at the front of `kitPdf`. */
    masterCount: number;
    /** masterCount + the terms' page count — what every footer says "of". */
    totalPages: number;
    /** Footer title, e.g. 'Catalog.ID Membership Kit'. */
    title: string;
    /** Roboto Regular, the face pdfmake draws its own footer with. */
    robotoTtf: Uint8Array;
}

/**
 * Append the terms to the kit and stamp the continued page numbers on them.
 * Pure: bytes in, bytes out — nothing touches the network or the disk.
 */
export async function mergeKit(input: MergeKitInput): Promise<Uint8Array> {
    const kit = await PDFDocument.load(input.kitPdf);
    const terms = await PDFDocument.load(input.termsPdf);
    const termsPageCount = terms.getPageCount();

    // A layout computed against a stale TERMS_ASSET.pages would number the kit
    // wrongly and print it before anyone noticed — refuse instead.
    if (input.masterCount + termsPageCount !== input.totalPages) {
        throw new Error(
            `terms page count ${termsPageCount} does not fit the kit layout `
            + `(${input.masterCount} master pages of ${input.totalPages} total)`);
    }

    kit.registerFontkit(fontkit);
    const font = await kit.embedFont(input.robotoTtf, { subset: true });

    const copied = await kit.copyPages(terms, terms.getPageIndices());
    for (const page of copied) kit.addPage(page);

    for (const stamp of termsFooterStamps({ ...input, termsPageCount })) {
        drawFooter(copied[stamp.pageIndex], stamp.text, font);
    }

    return kit.save();
}

/** The footer line each appended terms page gets: the kit numbering continues
 *  straight after the master pages — the unnumbered copy sheets in between are
 *  skipped, exactly as pdfmake skips them. */
export function termsFooterStamps(input: {
    masterCount: number; termsPageCount: number; totalPages: number; title: string;
}): KitFooterStamp[] {
    return Array.from({ length: input.termsPageCount }, (_, i) => ({
        pageIndex: i,
        // Same brand-prefix strip as the pdfmake footer (shared.ts).
        text: `${shortKitTitle(input.title)} — page ${input.masterCount + i + 1} of ${input.totalPages}`,
    }));
}

/** Baseline of pdfmake's footer line, in pt above the page's bottom edge. */
export function footerBaselineFromBottom(ascenderAtSize: number): number {
    return PAGE_BOTTOM_MARGIN - FOOTER_TOP_MARGIN - ascenderAtSize;
}

/** Left edge of a right-aligned footer string of the given width. */
export function footerX(pageWidth: number, textWidth: number): number {
    return pageWidth - FOOTER_SIDE_MARGIN - textWidth;
}

function drawFooter(page: PDFPage, text: string, font: PDFFont): void {
    const width = font.widthOfTextAtSize(text, FOOTER_FONT_SIZE);
    page.drawText(text, {
        x: footerX(page.getWidth(), width),
        y: footerBaselineFromBottom(ascenderAtSize(font, FOOTER_FONT_SIZE)),
        size: FOOTER_FONT_SIZE,
        font,
    });
}

/**
 * The font's ascender at a size, in pt — pdfkit's `ascender / 1000 * fontSize`
 * with `ascender = fontkit ascent × 1000 / unitsPerEm`. pdf-lib exposes no
 * ascent-only accessor (`heightAtSize` mixes scaled and raw units), so this
 * reaches through to the same fontkit Font pdf-lib embedded; the fallback is
 * Roboto Regular's own hhea ascent, which is what we always pass in.
 */
function ascenderAtSize(font: PDFFont, size: number): number {
    const embedded = (font as unknown as {
        embedder?: { font?: { ascent?: number; unitsPerEm?: number } };
    }).embedder?.font;
    const ascent = embedded?.ascent;
    const unitsPerEm = embedded?.unitsPerEm;
    const perMille = ascent && unitsPerEm ? (ascent * 1000) / unitsPerEm : ROBOTO_ASCENDER_PER_MILLE;
    return (perMille / 1000) * size;
}
